import React, { FC, useCallback, useEffect, useState } from 'react'

import {
  MAX_ACTIVE_GROUP_MEMBERS_NUMBER,
  pricePresenter,
  useRunCallbackOnEnteringViewport,
} from '@smarty-nx/code-utils'
import { Button, Level, Typography } from '@smarty-nx/ds'
import {
  DATA_LAYER_EVENTS,
  ORDER_TYPES,
  triggerEvent,
  triggerProductEvent,
} from '@smarty-nx/tracking'
import cn from 'classnames'

import { content } from './compare-group-plans-calculator.content'
import classes from './compare-group-plans-calculator.module.scss'
import { useBuildTimePlans, usePlansWithFallback } from '../../../hooks'
import { setMembersNumber } from '../../../state/groupPlans/group-plans.slice'
import {
  RootState,
  useAppDispatch,
  useAppSelector,
} from '../../../state/rootReducer'
import { triggerModuleLoadEvent } from '../../../utils/dataLayer'
import { getFamilyPlansSlugs } from '../../../utils/plans'
import GroupLandingPageCTA, {
  GroupLandingCTAType,
} from '../group-landing-page-cta'
import SelectGroupPlanDropdown, {
  GroupPlanOption,
  PlanType,
} from '../select-group-plan-dropdown'

type SelectedPlanData = {
  index: number
  type: PlanType
  originalPrice: number
  finalPrice: number
}

export const CompareGroupPlansCalculator: FC = () => {
  const dispatch = useAppDispatch()

  const buildTimePlans = useBuildTimePlans()
  const { list: plans, originalPlans } = usePlansWithFallback(
    getFamilyPlansSlugs(),
    buildTimePlans
  )
  const familyPlanOriginal = originalPlans.find(
    (p) => p.id === 'SM_EVERYONE_UNLIMITED'
  )
  const familyPlan = plans.find(
    (p) => p.priceData.id === 'SM_EVERYONE_UNLIMITED'
  )
  const groupOwnerPrice = familyPlan?.priceData.finalPrice.value || 0
  const groupMemberPrice = familyPlan?.priceData?.additionalSims?.value || 0
  const membersNumber = useAppSelector(
    (state: RootState) => state.groupPlans.membersNumber
  )
  const membersCounterValues: number[] = Array.from(
    { length: MAX_ACTIVE_GROUP_MEMBERS_NUMBER },
    (_, i) => i + 2
  )

  const [everyoneUnlimitedTotal, setEveryoneUnlimitedTotal] = useState<number>(
    groupOwnerPrice + groupMemberPrice
  )
  const [selectedOwnerPlan, setSelectedOwnerPlan] =
    useState<SelectedPlanData | null>(null)
  const [selectedMemberPlans, setSelectedMemberPlans] = useState<
    SelectedPlanData[]
  >([])
  const [mixAndMatchOriginalTotal, setMixAndMatchOriginalTotal] =
    useState<number>(0)
  const [mixAndMatchFinalTotal, setMixAndMatchFinalTotal] = useState<number>(0)

  const refUnlimited = useRunCallbackOnEnteringViewport(() => {
    triggerModuleLoadEvent(document, 'everyone-unlimited', 'popular-plans')
  })

  const refMixAndMatch = useRunCallbackOnEnteringViewport(() => {
    triggerModuleLoadEvent(document, 'mix-and-match', 'popular-plans')
  })

  useEffect(() => {
    const initialTotal = groupOwnerPrice + groupMemberPrice
    setEveryoneUnlimitedTotal(initialTotal)
  }, [groupOwnerPrice, groupMemberPrice, setEveryoneUnlimitedTotal])

  useEffect(() => {
    const planId = familyPlanOriginal?.id
    if (!planId) return

    triggerProductEvent(
      DATA_LAYER_EVENTS.PRODUCT_VIEW,
      familyPlanOriginal,
      undefined,
      ORDER_TYPES.GROUP_PLAN_NEW_USER_CHECKOUT
    )
  }, [familyPlanOriginal])

  useEffect(() => {
    triggerEvent({
      event: DATA_LAYER_EVENTS.PRODUCT_VIEW,
      order_type: ORDER_TYPES.GROUP_PLAN_NEW_USER_CHECKOUT,
      product: {
        id: 'groups-mix-and-match',
        order_type: ORDER_TYPES.GROUP_PLAN_NEW_USER_CHECKOUT,
      },
    })
  }, [])

  const calculateMixAndMatchTotal = useCallback(
    (
      counter: number,
      ownerPlan: SelectedPlanData | null,
      memberPlans: SelectedPlanData[]
    ) => {
      let originalTotal = 0
      let finalTotal = 0

      const visibleMemberPlans = memberPlans.filter(
        (plan) => plan.index <= counter - 2
      )
      visibleMemberPlans.forEach((plan) => {
        originalTotal = originalTotal + plan.originalPrice
        finalTotal = finalTotal + plan.finalPrice
      })
      if (ownerPlan) {
        originalTotal = originalTotal + ownerPlan.originalPrice
        if (visibleMemberPlans.length > 0) {
          finalTotal = finalTotal + ownerPlan.finalPrice
        } else {
          finalTotal = finalTotal + ownerPlan.originalPrice
        }
      }

      setMixAndMatchOriginalTotal(originalTotal)
      setMixAndMatchFinalTotal(finalTotal)
    },
    []
  )

  useEffect(() => {
    calculateMixAndMatchTotal(
      membersNumber,
      selectedOwnerPlan,
      selectedMemberPlans
    )
  }, [
    membersNumber,
    selectedOwnerPlan,
    selectedMemberPlans,
    calculateMixAndMatchTotal,
  ])

  const handleMembersCounter = (count: number) => {
    dispatch(setMembersNumber(count))
    const additionalMembers = count - 1
    const additionalMembersPrice = additionalMembers * groupMemberPrice
    const total = additionalMembersPrice + groupOwnerPrice
    setEveryoneUnlimitedTotal(total)

    const visibleMemberPlans = selectedMemberPlans.filter(
      (plan) => plan.index <= count - 2
    )
    setSelectedMemberPlans(visibleMemberPlans)
  }

  const handlePlanSelect = (
    value: GroupPlanOption,
    type: PlanType,
    index: number
  ) => {
    if (type === PlanType.Owner) {
      const ownerPlan: SelectedPlanData = {
        index,
        type,
        originalPrice: value.originalPrice,
        finalPrice: value.finalPrice,
      }
      setSelectedOwnerPlan(ownerPlan)
    } else {
      const newMemberPlans = [...selectedMemberPlans]
      const planIndex = newMemberPlans.findIndex(
        (plan) => plan.index === index && plan.type === type
      )

      if (planIndex === -1) {
        newMemberPlans.push({
          index,
          type,
          originalPrice: value.originalPrice,
          finalPrice: value.finalPrice,
        })
      } else {
        newMemberPlans[planIndex].originalPrice = value.originalPrice
        newMemberPlans[planIndex].finalPrice = value.finalPrice
      }
      setSelectedMemberPlans(newMemberPlans)
    }
  }

  return (
    <div>
      <div
        id="compare"
        className={classes.groupPlanCalculator}
        data-module-id="popular-plans"
        data-module_name="popular_plans"
      >
        <Typography
          variant="Kollektif/ExtraLarge"
          data-testid="group-plan-calculator-title-desktop"
          className={classes.desktop}
        >
          {content.title}
        </Typography>
        <div className={classes.mobile} style={{ textAlign: 'left' }}>
          <Typography
            variant="Kollektif/Large"
            data-testid="group-plan-calculator-title-mobile"
          >
            {content.titleMobile}
          </Typography>
        </div>
        <div
          data-testid="group-plan-calculator-description"
          className={classes.description}
        >
          {content.description}
        </div>
        <div className={classes.counterWrapper} data-testid="members-counter">
          {membersCounterValues.map((count, index) => (
            <Button
              type="button"
              level={Level.Tertiary}
              size="tiny"
              className={cn(classes.counterButton, {
                [classes.selected]: membersNumber === count,
              })}
              key={index}
              onClick={() => handleMembersCounter(count)}
              role="region"
              aria-roledescription="carousel"
              dataTestId={`members-counter-button-${count.toString()}`}
            >
              {count.toString()}
            </Button>
          ))}
        </div>
        <div
          className={classes.calculatorWrapper}
          data-testid="group-plan-calculator"
        >
          <div className={classes.calculatorColumnWrapper} ref={refUnlimited}>
            <div
              className={classes.calculatorColumn}
              data-item-id="everyone-unlimited"
              data-type="product"
              data-position="1"
              data-asset_name="everyone-unlimited"
              data-asset_category="group-plan"
              data-asset_value_charge-type="monthly"
              data-asset_value_display-gross={everyoneUnlimitedTotal}
              data-asset_discount_value_display-gross="0"
            >
              <div className={classes.columnContent}>
                <div
                  className={cn(classes.topSection, classes.everyoneUnlimited)}
                >
                  <div className={classes.topSectionItem}>
                    <Typography
                      component="h4"
                      variant="Kollektif/Large"
                      data-testid="everyone-unlimited-title"
                    >
                      {content.everyoneUnlimitedTitle}
                    </Typography>
                    <Typography
                      component="span"
                      variant="Kollektif/ExtraSmallEmphasis"
                      data-testid="everyone-unlimited-subtitle"
                    >
                      {content.everyoneUnlimitedSubtitle}
                    </Typography>
                  </div>
                  <div className={classes.topSectionItem}>
                    <Typography
                      variant="Kollektif/ExtraSmall"
                      dataTestId="everyone-unlimited-description"
                    >
                      {content.everyoneUnlimitedDescription(
                        groupOwnerPrice,
                        groupMemberPrice
                      )}
                    </Typography>
                  </div>
                </div>
                <div className={classes.bottomSection}>
                  <div className={classes.bottomSectionItem}>
                    <Typography
                      component="span"
                      variant="Kollektif/ExtraSmallEmphasis"
                      dataTestId="group-owner-label"
                    >
                      {content.groupOwnerLabel}
                    </Typography>
                    <Typography
                      component="span"
                      variant="Kollektif/ExtraSmallEmphasis"
                      dataTestId="group-owner-cost"
                    >
                      {pricePresenter(groupOwnerPrice)}
                    </Typography>
                  </div>
                  <div className={classes.desktop}>
                    {Array.from(
                      { length: membersNumber - 1 },
                      (_, k) => k + 1
                    ).map((_, index) => (
                      <div
                        className={cn(
                          classes.bottomSectionItem,
                          classes.bottomSectionItemLeft
                        )}
                        key={index}
                      >
                        <Typography
                          component="span"
                          variant="Kollektif/ExtraSmall"
                          dataTestId="group-member-label-desktop"
                        >
                          {content.groupMemberLabel}
                        </Typography>
                        <Typography
                          component="span"
                          variant="Kollektif/ExtraSmallEmphasis"
                          dataTestId="group-member-cost-desktop"
                        >
                          {pricePresenter(groupMemberPrice)}
                        </Typography>
                      </div>
                    ))}
                  </div>
                  <div className={classes.mobile}>
                    <div className={classes.bottomSectionItem}>
                      <Typography
                        component="span"
                        variant="Kollektif/ExtraSmall"
                        dataTestId="group-member-label-mobile"
                      >
                        {`+ ${membersNumber - 1} ${
                          membersNumber - 1 === 1 ? 'member' : 'members'
                        }`}
                      </Typography>
                      <Typography
                        component="span"
                        variant="Kollektif/ExtraSmallEmphasis"
                        dataTestId="group-member-cost-mobile"
                      >
                        {pricePresenter((membersNumber - 1) * groupMemberPrice)}
                      </Typography>
                    </div>
                  </div>
                  <div className={classes.line} />
                  <div className={classes.bottomSectionItem}>
                    <Typography
                      variant="Kollektif/ExtraSmall"
                      dataTestId="everyone-unlimited-total-label"
                    >
                      {content.everyoneUnlimitedTotalLabel}
                    </Typography>
                    <Typography
                      component="span"
                      variant="Kollektif/ExtraSmallEmphasis"
                      dataTestId="everyone-unlimited-total-value"
                    >
                      {pricePresenter(everyoneUnlimitedTotal)}
                    </Typography>
                  </div>
                </div>
              </div>
            </div>
            <div className={classes.ctaWrapper}>
              <GroupLandingPageCTA
                buttonType={GroupLandingCTAType.EVERYONE_UNLIMITED}
                dataTestIdPrefix="calculator"
              />
            </div>
          </div>
          <div className={classes.calculatorColumnWrapper} ref={refMixAndMatch}>
            <div
              className={classes.calculatorColumn}
              data-item-id="mix-and-match"
              data-type="product"
              data-position="1"
              data-asset_name="mix-and-match"
              data-asset_category="group-plan"
              data-asset_value_charge-type="monthly"
              data-asset_value_display-gross={mixAndMatchOriginalTotal}
              data-asset_discount_value_display-gross={
                mixAndMatchOriginalTotal - mixAndMatchFinalTotal
              }
            >
              <div className={classes.columnContent}>
                <div className={cn(classes.topSection, classes.mixAndMatch)}>
                  <div className={classes.topSectionItem}>
                    <Typography
                      component="h4"
                      variant="Kollektif/Large"
                      dataTestId="mix-and-match-title"
                    >
                      {content.mixAndMatchTitle}
                    </Typography>
                    <Typography
                      component="span"
                      variant="Kollektif/ExtraSmallEmphasis"
                      dataTestId="mix-and-match-subtitle"
                    >
                      {content.mixAndMatchSubtitle}
                    </Typography>
                  </div>
                  <div className={classes.topSectionItem}>
                    <Typography
                      variant="Kollektif/ExtraSmall"
                      dataTestId="mix-and-match-description"
                    >
                      {content.mixAndMatchDescription}
                    </Typography>
                  </div>
                </div>
                <div className={classes.bottomSection}>
                  <div className={classes.bottomSectionItem}>
                    <Typography
                      component="span"
                      variant="Kollektif/ExtraSmallEmphasis"
                      dataTestId="group-owner-label"
                    >
                      {content.groupOwnerLabel}
                    </Typography>
                    <SelectGroupPlanDropdown
                      onSelect={handlePlanSelect}
                      type={PlanType.Owner}
                    />
                  </div>
                  {Array.from(
                    { length: membersNumber - 1 },
                    (_, k) => k + 1
                  ).map((_, index) => (
                    <div className={classes.bottomSectionItem} key={index}>
                      <Typography
                        component="span"
                        variant="Kollektif/ExtraSmall"
                        data-testid="group-member-label"
                      >
                        {content.groupMemberLabel}
                      </Typography>
                      <SelectGroupPlanDropdown
                        onSelect={handlePlanSelect}
                        type={PlanType.Member}
                        index={index}
                      />
                    </div>
                  ))}
                  <div className={classes.line} />
                  <div className={classes.bottomSectionItem}>
                    <Typography
                      variant="Kollektif/ExtraSmall"
                      dataTestId="mix-and-match-total-label"
                    >
                      {content.mixAndMatchTotalLabel}
                    </Typography>
                    <div>
                      <span style={{ textDecoration: 'line-through' }}>
                        <Typography
                          component="span"
                          variant="Kollektif/ExtraSmall"
                          dataTestId="mix-and-match-original-total-value"
                        >
                          {selectedMemberPlans.length > 0 &&
                            mixAndMatchOriginalTotal > 0 &&
                            mixAndMatchOriginalTotal !==
                              mixAndMatchFinalTotal &&
                            pricePresenter(mixAndMatchOriginalTotal)}
                        </Typography>
                      </span>
                      &nbsp;
                      <Typography
                        component="span"
                        variant="Kollektif/ExtraSmallEmphasis"
                        dataTestId="mix-and-match-final-total-value"
                      >
                        {pricePresenter(mixAndMatchFinalTotal)}
                      </Typography>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className={classes.ctaWrapper}>
              <GroupLandingPageCTA
                buttonType={GroupLandingCTAType.MIX_AND_MATCH}
                dataTestIdPrefix="calculator"
              />
            </div>
          </div>
        </div>
        <div>
          <Typography
            component="span"
            variant="Kollektif/TinyEmphasis"
            dataTestId="group-plan-calculator-bottom-note-label"
          >
            {content.bottomNoteLabel}
          </Typography>
          &nbsp;
          <Typography
            component="span"
            variant="Kollektif/Tiny"
            dataTestId="group-plan-calculator-title-bottom-note-text"
          >
            {content.bottomNoteText}
          </Typography>
        </div>
      </div>
    </div>
  )
}

export default CompareGroupPlansCalculator
